import React from 'react';

import { useTranslation } from 'i18n/hooks';
import { SpacedContainer } from 'revibe-ui';
import { SHOP_ROUTES } from 'routes';

import { getDynamicRouteUrl } from 'modules/common/utils';

import { ItemPhoto } from 'modules/item/components/ItemPhoto';
import { ItemPrice } from 'modules/item/components/ItemPrice';
import { ItemSchemaOrg } from 'modules/item/components/ItemSchemaOrg';
import { getPercentageCutFromPrice } from 'modules/item/utils/itemPrice';
import { getIsItemInStock, getItemStock } from 'modules/item/utils/itemStock';
import { FavoriteItemButton } from 'modules/catalog/components/FavoriteItemButton';
import { ShopBreadcrumb } from 'modules/catalog/components/ShopBreadcrumb';
import { formatPrice } from 'modules/catalog/utils/formatPrice';

import { Link } from 'ui/components/Link';
import { Tag } from 'ui/components/Tag';

import { ItemAdditionalInfoAccordion } from './ItemAdditionalInfoAccordion';

import { MarketplaceAPI } from 'revibe-api';

import { ItemAddToCart } from './ItemAddToCart';

type Props = {
  item: MarketplaceAPI.Item;
};

export const ItemDetailedView = ({ item }: Props) => {
  const { tu, t } = useTranslation('shop');

  const availableStock = item.stock;

  const isLastItemAvailable =
    getItemStock(availableStock) === 1 && !item.available_for_preorder;
  const percentageCut = getPercentageCutFromPrice(
    item.price,
    item.selling_price || item.price
  );
  const isAvailable =
    getIsItemInStock(availableStock) || item.available_for_preorder;

  return (
    <div>
      <ItemSchemaOrg item={item} itemIsAvailable={isAvailable} />
      <ShopBreadcrumb sex={item.sex || 'all'} category={item.category} />
      <SpacedContainer className="">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          <div className="relative">
            <ItemPhoto itemName={item.name} photos={item.photos} />
            <div className="child:h-fit absolute top-4 left-4 flex flex-wrap items-center gap-2 lg:right-4 lg:justify-end">
              {item.available_for_preorder && <Tag>{tu('preorder')}</Tag>}
              {isLastItemAvailable && (
                <Tag type="error">{tu('last-available')}</Tag>
              )}
              {percentageCut > 0 ? (
                <Tag>
                  {formatPrice(percentageCut, 0)} &#37; {t('discount', 'shop')}
                </Tag>
              ) : null}
            </div>
          </div>
          <div className="h-full border-black lg:border-l">
            <div className="top-[3rem] h-fit lg:sticky">
              <SpacedContainer className="mx-4 max-w-2xl pt-4 lg:mx-10 lg:pt-10">
                <SpacedContainer spacing="small">
                  <Link
                    hasDecoration={false}
                    to={getDynamicRouteUrl(SHOP_ROUTES.SELLER_SLUG, {
                      slug: item.seller.slug,
                    })}
                  >
                    {item.seller.name}
                  </Link>
                  <h1 className="font-formula text-2xl xl:text-4xl">
                    {item.name}
                  </h1>

                  <div className="text-2xl">
                    <ItemPrice
                      price={item.price}
                      sellingPrice={item.selling_price}
                      shouldDisplayVAT
                    />
                  </div>
                </SpacedContainer>
                <ItemAddToCart item={item} key={item.id} />
                <div className="mx-auto">
                  <FavoriteItemButton hasText item={item} />
                </div>
              </SpacedContainer>
              <div className="mt-24">
                <div className="whitespace-pre-wrap border-y border-black">
                  <ItemAdditionalInfoAccordion item={item} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </SpacedContainer>
    </div>
  );
};
